import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PrivateService } from 'app/core/services/private.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-dialog-edit-loan-line',
  templateUrl: './dialog-edit-loan-line.component.html',
  styleUrls: ['./dialog-edit-loan-line.component.scss']
})
export class DialogEditLoanLineComponent implements OnInit {

  loanLineForm: FormGroup;
  isLoading: boolean = false;
  loanLineData: any;
  loanHeaderData: any;

  constructor(
    public dialogRef: MatDialogRef<DialogEditLoanLineComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private privateService: PrivateService,
    private toastr: ToastrService
  ) {
    this.loanLineData = data.loanLine;
    this.loanHeaderData = data.loanHeader;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    // Convert date from MM-DD-YYYY to YYYY-MM-DD
    const formattedDate = this.convertDateFormat(this.loanLineData.dueDate);

    this.loanLineForm = this.formBuilder.group({
      hr_loanlines_id: [this.loanLineData.hr_loanlines_id],
      dueDate: [formattedDate, Validators.required],
      toPay: [this.loanLineData.toPay, [Validators.required, Validators.min(0)]],
      isPaid: [{ value: this.loanLineData.isPaid, disabled: true }],
      status: [{ value: this.loanLineData.isPaid ? 'Paid' : 'Pending', disabled: true }]
    });
  }

  convertDateFormat(dateStr: string): string {
    if (!dateStr) return '';

    // Split the date string (assuming format: MM-DD-YYYY)
    const parts = dateStr.split('-');

    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Return in YYYY-MM-DD format
      return `${year}-${month}-${day}`;
    }

    return dateStr;
  }

  onSave(): void {
    if (this.loanLineForm.invalid) {
      this.showNotificationError('Please fill in all required fields correctly');
      return;
    }

    this.isLoading = true;
    const formValue = this.loanLineForm.getRawValue();

    // Convert date from YYYY-MM-DD back to MM-DD-YYYY for backend
    const backendDate = this.convertDateToBackendFormat(formValue.dueDate);

    const loanLineData = {
      hr_loanlines_id: formValue.hr_loanlines_id,
      dueDate: backendDate,
      toPay: formValue.toPay
    };

    this.privateService.updateLoanLine(loanLineData).subscribe({
      next: (res) => {
        this.showNotification('Loan installment updated successfully');
        this.dialogRef.close(true);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error updating loan line:', err);
        let errorMsg = 'Failed to update loan installment';
        if (err.error && err.error.message) {
          errorMsg = err.error.message;
        }
        this.showNotificationError(errorMsg);
        this.isLoading = false;
      }
    });
  }

  convertDateToBackendFormat(dateStr: string): string {
    if (!dateStr) return '';

    // Split the date string (assuming format: YYYY-MM-DD from input)
    const parts = dateStr.split('-');

    if (parts.length === 3) {
      const [year, month, day] = parts;
      // Return in MM-DD-YYYY format
      return `${month}-${day}-${year}`;
    }

    return dateStr;
  }
  
  onCancel(): void {
    this.dialogRef.close(false);
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  showNotification(text: string): void {
    this.toastr.success('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-success alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }

  showNotificationError(text: string): void {
    this.toastr.error('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-danger alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }
}